import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./dashboard.scss";
import { setSidebarStatus } from "actions/user";
import Arrow from "assets/images/leftArr.png";
import { useDispatch, useSelector } from "react-redux";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { useHistory } from "react-router";
import { getDashboardData } from "api/dashboard/getDashboardData";
import FooterComponent from "components/LandingPageTemplateComponents/FooterComponent";
import { computeFinalPermissions } from 'helpers/finalPermissions';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS_FLOW = {
  manual: "#94a3b8",        // grey-blue
  autoApproved: "#0F62FE",  // brand blue
  autoEndToEnd: "#22c55e",  // green
  aiRejected: "#ef4444",    // red
};
const COLORS_MODE = {
  autoEndToEnd: "#22c55e",
  autoWithApproval: "#0F62FE",
  manual: "#94a3b8",
};
const COLORS_PROFILE = {
  generated: "#22c55e",
  failed: "#ef4444",
  notYet: "#cbd5e1",
};

function StatCard({ label, value, accent = "#0F62FE", sub }) {
  return (
    <div className="dashboard-cards card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: accent, lineHeight: 1.1 }}>
              {value == null ? "—" : value}
            </div>
            {sub && <div style={{ fontSize: 12, color: "#94a3b8" }}>{sub}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, height = 220 }) {
  return (
    <div className="card border-0 mb-3">
      <div className="card-body">
        <p className="chart-header mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
          {title}
        </p>
        <div style={{ width: "100%", height }}>{children}</div>
      </div>
    </div>
  );
}

function bucketsToPieData(buckets, labelMap, colorMap) {
  return Object.keys(labelMap)
    .map((k) => ({ name: labelMap[k], key: k, value: buckets?.[k] || 0 }))
    .filter((d) => d.value > 0)
    .map((d) => ({ ...d, color: colorMap[d.key] }));
}

export default function Dashboard() {
  const dispatch = useDispatch();
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = computeFinalPermissions({
    userInfo: userInfoRedux,
    allPermission: allPermissionRedux,
    companyPermission: companyPermissionRedux,
    userPermission: usersPermissionRedux,
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyId, setCompanyId] = useState("");

  const fetchData = useCallback(async (cid) => {
    setLoading(true);
    setError(null);
    try {
      const url = cid ? `/analytics?companyId=${encodeURIComponent(cid)}` : "/analytics";
      const { get } = await import("api/index");
      const res = await get(url);
      setData(res);
    } catch (e) {
      setError(e?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(companyId || null);
  }, [fetchData, companyId]);

  const posts = data?.posts || {};
  const flowBuckets = data?.flowBuckets || {};
  const calls = data?.calls || { llm: {}, platform: {} };
  const automation = data?.automation || {};
  const brandProfiles = data?.brandProfiles || {};
  const brandAutomationMode = data?.brandAutomationMode || {};
  const topEngagedPosts = data?.topEngagedPosts || [];
  const companies = data?.companies || [];
  const cost = data?.cost || { totalUSD: 0, perPostUSD: 0, totalTokensIn: 0, totalTokensOut: 0, byModel: [] };

  // Currency display helper — small USD values need 4 decimals; large need 2.
  const fmtUSD = (n) => {
    const v = Number(n) || 0;
    if (v >= 10) return `$${v.toFixed(2)}`;
    if (v >= 0.01) return `$${v.toFixed(4)}`;
    if (v > 0) return `$${v.toFixed(6)}`;
    return "$0.00";
  };
  const fmtTokens = (n) => {
    const v = Number(n) || 0;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
    return String(v);
  };

  // Cost breakdown chart: per provider/model bar, sorted by usd desc.
  const costByModelData = (cost.byModel || []).map((row) => ({
    name: `${row.kind || 'text'} · ${row.model || row.provider}`,
    usd: row.usd || 0,
    calls: row.calls || 0,
  }));

  // Role-specific cost breakdown:
  // super admin → cost.byCompany; company admin → cost.byUser; user → none
  const isCompanyAdmin = !!userInfoRedux?.companyAdmin && !userInfoRedux?.superAdmin;
  const isRegularUser = !userInfoRedux?.companyAdmin && !userInfoRedux?.superAdmin;
  const costByCompany = data?.cost?.byCompany || [];
  const costByUser = data?.cost?.byUser || [];
  const companyNameById = useMemo(() => {
    const m = {};
    companies.forEach((c) => { m[c._id] = c.name; });
    return m;
  }, [companies]);

  const flowPieData = useMemo(
    () =>
      bucketsToPieData(
        flowBuckets,
        {
          manual: "Manual",
          autoApproved: "AI + human approval",
          autoEndToEnd: "Auto end-to-end",
          aiRejected: "AI rejected",
        },
        COLORS_FLOW
      ),
    [flowBuckets]
  );

  const modePieData = useMemo(
    () =>
      bucketsToPieData(
        brandAutomationMode,
        {
          autoEndToEnd: "Auto end-to-end",
          autoWithApproval: "Auto with approval",
          manual: "Manual",
        },
        COLORS_MODE
      ),
    [brandAutomationMode]
  );

  const profilePieData = useMemo(
    () =>
      bucketsToPieData(
        brandProfiles,
        { generated: "Generated", failed: "Failed", notYet: "Not yet" },
        COLORS_PROFILE
      ),
    [brandProfiles]
  );

  const callsBarData = useMemo(
    () => [
      { name: "LLM calls / post", value: calls?.llm?.perPost || 0 },
      { name: "Platform calls / post", value: calls?.platform?.perPost || 0 },
    ],
    [calls]
  );

  const automationBarData = useMemo(
    () => [
      { name: "Succeeded", value: automation.succeeded || 0, fill: "#22c55e" },
      { name: "Failed", value: automation.failed || 0, fill: "#ef4444" },
    ],
    [automation]
  );

  const isSuperAdmin = !!userInfoRedux?.superAdmin;
  const scopeLabel =
    data?.scope?.role === "superAdmin"
      ? data?.scope?.selectedCompanyId
        ? `Super admin · ${companies.find((c) => c._id === data.scope.selectedCompanyId)?.name || "selected company"}`
        : "Super admin · all companies"
      : data?.scope?.role === "companyAdmin"
        ? "Company admin · your company"
        : "Your brands";

  return (
    <div style={{ background: "#eaeaea" }}>
      <div className="inner-header-position">
        <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <span
              style={{ alignItems: "center", display: "flex", cursor: "pointer" }}
              onClick={() => dispatch(setSidebarStatus(true))}
            >
              <div className="cursor-pointer">
                <img src={Arrow} className="left-arrow-img" alt="" />
              </div>
              <p className="my-heading f-14 ml-3 mr-3">Side Menu</p>
            </span>
          </div>
          <div className="projActionButtons d-flex flex-wrap" style={{ maxWidth: "100vw" }}>
            <div className="btn-tool-group">
              {(userInfoRedux?.superAdmin || userInfoRedux?.companyAdmin) && (
                <>
                  <button
                    onClick={() => {
                      if (FinalPermissions?.includes(ALL_PERMISSIONS.PROJECT_VIEW)) {
                        history.push(`/admin/projects-settings`);
                      } else if (FinalPermissions?.includes(ALL_PERMISSIONS.STATUS_VIEW)) {
                        history.push(`/admin/statuses`);
                      } else {
                        history.push(`/admin/user-permissions`);
                      }
                    }}
                    className="bg-bid px-3 mr-0"
                  >
                    Admin
                  </button>
                  {isSuperAdmin && (
                    <>
                      {/* "Companies" link lives in the sidebar Admin
                          section → /super-admin (the existing canonical
                          page). Package toggle now happens on the
                          per-company /super-admin/permissions/:id view. */}
                      <div className="vr mx-3"></div>
                      <button
                        onClick={() => history.push(`/worker-health`)}
                        className="bg-bid px-3 mr-0"
                        title="Worker queues across all companies"
                      >
                        Worker health
                      </button>
                    </>
                  )}
                  {/* API cost button — super-admin only. Cost data is
                      never shown to tenant admins (platform-fee
                      visibility leak). Was guarded by apiCostView
                      perm which a companyAdmin could be granted. */}
                  {isSuperAdmin && (
                    <>
                      <div className="vr mx-3"></div>
                      <button
                        onClick={() => history.push(`/admin-cost-control`)}
                        className="bg-bid px-3 mr-0"
                        title="API cost across all companies"
                      >
                        API cost & control
                      </button>
                    </>
                  )}
                  {/* Email Management lives in the sidebar (like Social Posts),
                      not as a Dashboard button — keeps the dashboard focused on
                      admin tooling (Companies / Worker / Cost) and matches the
                      Posts navigation pattern. */}
                  <div className="vr mx-3"></div>
                </>
              )}
              <button
                disabled={loading}
                onClick={() => fetchData(companyId || null)}
                className="px-3 bg-white text-bid shadow-hover"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary mx-3">
        <div className="h-screen flex-grow-1 overflow-y-lg-auto">
          <main className="py-6 bg-surface-secondary">
            <div className="container-fluid">
              {/* Scope banner */}
              <div className="row mt-3">
                <div className="col-12 d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 12 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 22 }}>Prigmar dashboard</h2>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {scopeLabel}
                      {data?.scope?.brandCount != null && (
                        <span> · {data.scope.brandCount} brand{data.scope.brandCount === 1 ? "" : "s"} in scope</span>
                      )}
                    </div>
                  </div>
                  {isSuperAdmin && (
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <label style={{ fontSize: 13, color: "#475569", margin: 0 }}>Filter by company</label>
                      <select
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        className="form-control"
                        style={{ width: 240 }}
                      >
                        <option value="">All companies</option>
                        {companies.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="alert alert-danger">{error}</div>
                  </div>
                </div>
              )}

              {/* Stat cards */}
              <div className="row g-3 mt-3">
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Posts scheduled" value={posts.scheduled} accent="#0F62FE" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Posts published" value={posts.succeeded} accent="#22c55e" sub={
                    posts.partiallyPosted ? `+${posts.partiallyPosted} partial` : null
                  } />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Posts failed" value={posts.failed} accent="#ef4444" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Awaiting approval" value={posts.awaitingApproval} accent="#f59e0b" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Rejected by reviewer" value={posts.rejected} accent="#64748b" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="In flight" value={posts.inFlight} accent="#0F62FE" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard
                    label="Brand profiles generated"
                    value={brandProfiles.generated}
                    accent="#22c55e"
                    sub={`${brandProfiles.failed || 0} failed · ${brandProfiles.notYet || 0} pending`}
                  />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard label="Posts total" value={posts.total} accent="#283f4b" />
                </div>
              </div>

              {/* Cost cards — super-admin only. Tenant admins never see
                  provider $ figures or token counts (cost knowledge is
                  internal-only). */}
              {isSuperAdmin && (
              <div className="row g-3 mt-1">
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard
                    label="AI cost (total)"
                    value={fmtUSD(cost.totalUSD)}
                    accent="#0F62FE"
                    sub={`Avg ${fmtUSD(cost.perPostUSD)} / post`}
                  />
                </div>
                <div className="col-xl-3 col-sm-6 col-12 mb-3">
                  <StatCard
                    label="Tokens in"
                    value={fmtTokens(cost.totalTokensIn)}
                    accent="#283f4b"
                    sub={`${fmtTokens(cost.totalTokensOut)} tokens out`}
                  />
                </div>
                <div className="col-xl-6 col-sm-12 col-12 mb-3">
                  <ChartCard title="AI cost by model" height={180}>
                    {costByModelData.length === 0 ? (
                      <div style={{ fontSize: 13, color: "#64748b", padding: 12 }}>
                        No AI cost data yet. Cost accrues as the worker generates posts (text + image + video).
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={costByModelData} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtUSD} />
                          <YAxis dataKey="name" type="category" width={160} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v, name) => name === "usd" ? fmtUSD(v) : v} />
                          <Bar dataKey="usd" fill="#0F62FE" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </ChartCard>
                </div>
              </div>
              )}

              {/* Role-specific cost breakdown */}
              {isSuperAdmin && costByCompany.length > 0 && (
                <div className="row g-3 mt-1">
                  <div className="col-12 mb-3">
                    <div className="card border-0">
                      <div className="card-body">
                        <p className="chart-header mb-3" style={{ fontSize: 14, fontWeight: 600 }}>
                          AI cost by company
                        </p>
                        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th>Company</th>
                              <th className="text-right">Posts</th>
                              <th className="text-right">AI cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costByCompany.map((r) => (
                              <tr key={r.companyId}>
                                <td>{companyNameById[r.companyId] || r.companyId}</td>
                                <td className="text-right">{r.posts}</td>
                                <td className="text-right"><strong>{fmtUSD(r.usd)}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI-cost-by-team-member was originally companyAdmin
                  too, but cost figures are now strictly super-admin —
                  tenant company admins should not see provider $. */}
              {isSuperAdmin && costByUser.length > 0 && (
                <div className="row g-3 mt-1">
                  <div className="col-12 mb-3">
                    <div className="card border-0">
                      <div className="card-body">
                        <p className="chart-header mb-3" style={{ fontSize: 14, fontWeight: 600 }}>
                          AI cost by team member
                        </p>
                        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                          <thead>
                            <tr>
                              <th>Team member</th>
                              <th className="text-right">Posts</th>
                              <th className="text-right">AI cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costByUser.map((r) => (
                              <tr key={r.userId}>
                                <td>
                                  <div style={{ fontWeight: 600 }}>
                                    {r.name || r.userId}
                                  </div>
                                  {r.email && (
                                    <div className="text-muted" style={{ fontSize: 11 }}>
                                      {r.email}
                                    </div>
                                  )}
                                </td>
                                <td className="text-right">{r.posts}</td>
                                <td className="text-right"><strong>{fmtUSD(r.usd)}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Charts row */}
              <div className="row g-3 mt-2">
                <div className="col-xl-4 col-md-6 col-12">
                  <ChartCard title="Post flow buckets">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={flowPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                          {flowPieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>
                <div className="col-xl-4 col-md-6 col-12">
                  <ChartCard title="Brand automation mode">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={modePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                          {modePieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>
                <div className="col-xl-4 col-md-6 col-12">
                  <ChartCard title="Brand profile coverage">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={profilePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                          {profilePieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>
              </div>

              {/* Charts row 2 */}
              <div className="row g-3 mt-2">
                <div className="col-xl-4 col-md-6 col-12">
                  <ChartCard title="Calls per post (average)">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={callsBarData}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#0F62FE" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: -8 }}>
                    Totals: LLM <strong>{calls?.llm?.total || 0}</strong>
                    {" · "}
                    Platform <strong>{calls?.platform?.total || 0}</strong>
                  </div>
                </div>
                <div className="col-xl-4 col-md-6 col-12">
                  <ChartCard title="Automated post outcome">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={automationBarData}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value">
                          {automationBarData.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>
                <div className="col-xl-4 col-12">
                  <ChartCard title="Top engaged posts (last 14 days)" height={260}>
                    {topEngagedPosts.length === 0 ? (
                      <div style={{ fontSize: 13, color: "#64748b", padding: 12 }}>
                        No engagement data yet. The worker's engagement scheduler refreshes daily;
                        once a post has at least one platform like/comment/share, it shows up here.
                      </div>
                    ) : (
                      <ul style={{ margin: 0, padding: 0, listStyle: "none", maxHeight: 260, overflowY: "auto" }}>
                        {topEngagedPosts.map((p) => (
                          <li key={p._id} style={{ borderBottom: "1px solid #eef2f7", padding: "8px 0", fontSize: 13 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <strong style={{ flex: 1 }}>{p.title || "(untitled)"}</strong>
                              <span style={{ color: "#0F62FE", fontWeight: 600 }}>
                                {p.analytics?.score || 0}
                              </span>
                            </div>
                            <div style={{ color: "#64748b", fontSize: 12 }}>
                              {(p.platforms || []).join(", ")} · ❤ {p.analytics?.likes || 0} · 💬 {p.analytics?.comments || 0} · 🔁 {p.analytics?.shares || 0}
                            </div>
                            {p.text && (
                              <div style={{ color: "#475569", fontSize: 12, marginTop: 2 }}>
                                {p.text.slice(0, 120)}{p.text.length > 120 ? "…" : ""}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </ChartCard>
                </div>
              </div>

              {loading && (
                <div className="row mt-2">
                  <div className="col-12" style={{ textAlign: "center", color: "#64748b", fontSize: 13 }}>
                    Loading…
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      <div className="mt-3">
        <FooterComponent />
      </div>
    </div>
  );
}
